import { Box, Button, Divider, Grid, Paper, Skeleton, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { DonorStats, getDonorStats } from 'src/shared/services/donorService';
import { AmountWithCurrency } from 'src/shared/components/atoms/amountWithCurrency';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import SearchField from 'src/shared/components/form/searchField';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { useTranslation } from 'react-i18next';
import { PersonAdd } from '@mui/icons-material';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { useNavigate } from 'react-router-dom';
import { PageFilterSection } from 'src/shared/components/organisms/pageFilterSection';
import { DonorDataGrid } from '../components/donorDataGrid';
import { DonorCreateModal } from '../components/donorCreateModal';
import DonorTabs, { DonorTabValue } from '../components/donorTabs';

export const DonorsListPage = () => {
  const { t } = useTranslation(['gift']);

  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [searchString, setSearchString] = useState<string>('');
  const [tempSearchString, setTempSearchString] = useState<string>('');
  const [createMode, setCreateMode] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<DonorTabValue>('active');
  const [stats, setStats] = useState<DonorStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    setStatsLoading(true);
    getDonorStats()
      .then((res) => setStats(res.data))
      .catch(() => setStats(null))
      .finally(() => setStatsLoading(false));
  }, []);

  useEffect(() => {
    if (tempSearchString === '') {
      setSearchString('');
    }
    if (tempSearchString.length >= 2) {
      setSearchString(tempSearchString);
    } else {
      setSearchString('');
    }
  }, [tempSearchString]);

  const loadingCallback = useCallback((isLoading: boolean) => {
    setLoading(isLoading);
  }, []);

  return (
    <>
      <GridWrapper>
        <Grid item xs>
          <MainContentHeader title={t('gift:donors')} />
        </Grid>
        <Grid item xs='auto' style={{ display: 'flex', justifyContent: 'flex-end', gridColumnGap: '5px' }}>
          <Button
            onClick={() => setCreateMode(true)}
            variant='contained'
            color='primary'
            startIcon={<PersonAdd />}
            disableElevation
            disabled={loading}
          >
            {t('gift:createDonor.header')}
          </Button>
        </Grid>
        <Grid item xs={12}>
          <Paper>
            <PageFilterSection leftContent={<SearchField onSearch={(newSearchString) => setTempSearchString(newSearchString)} />} />
            <Box sx={{ display: 'flex', gap: 2, px: 2, pt: 2, pb: 2 }}>
              {[
                { label: 'Aktiva månadsgivare', value: stats ? `${stats.activeDonors}` : undefined },
                {
                  label: 'Totalt månadsbelopp (aktiva)',
                  value: stats ? <AmountWithCurrency amount={stats.totalMonthlyAmount} decimals={0} /> : undefined,
                },
                { label: 'Snitt per givare', value: stats ? <AmountWithCurrency amount={stats.averageMonthlyAmount} decimals={0} /> : undefined },
                { label: 'Nya i år', value: stats ? `${stats.newThisYear}` : undefined },
              ].map((kpi) => (
                <Box
                  key={kpi.label}
                  sx={{
                    flex: 1,
                    p: 1.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant='body2' color='text.secondary' sx={{ lineHeight: 1.2 }}>
                    {kpi.label}
                  </Typography>
                  {statsLoading ? (
                    <Skeleton width='60%' height={32} />
                  ) : (
                    <Typography variant='h5' sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                      {kpi.value}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
            <Grid container justifyContent='space-between'>
              <Grid item xs='auto'>
                <DonorTabs initialValue={activeTab} callback={(val) => setActiveTab(val)} />
              </Grid>
            </Grid>
            <Divider />
            <DonorDataGrid loadingCallback={loadingCallback} searchString={searchString} statusFilter={activeTab} />
          </Paper>
        </Grid>
      </GridWrapper>
      {createMode && (
        <DonorCreateModal
          onClose={() => setCreateMode(false)}
          onSave={(id?: number) => {
            setCreateMode(false);
            if (id) navigate(`${RoutesEnum.DonorsDetail}/${id}`);
          }}
          modalOpen={createMode}
        />
      )}
    </>
  );
};
